/**
 * Promotion Overview Filters
 */
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DatePickerMui from '../DatePickerMui';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import SelectDropdown from '../SelectDropdown';
import Button from '../Button';
import { styles } from '../PromotionListingTable/style';
import { formatDateToMDY } from '../../constants/utils';
import { PROMOTION_TYPES } from '../../pages/createPromotion/constants';
const ClearFilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.promotionStatus.closed,
  marginRight: '6px',
}));

const PromotionOverviewFilters = ({
  handleSelectedFilters = (filters) => {},
  handleClearedFilterState = () => {},
}) => {
  const [resetStartDate, setResetStartDate] = useState(false);
  const [resetEndDate, setResetEndDate] = useState(false);
  const [resetBranch, setResetBranch] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const { userStores, isAdmin } = useSelector((state) => state.auth);
  const statusFilters = [
    { label: 'Open', value: 'OPEN' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Published', value: 'PUBLISHED' },
    { label: 'Frozen', value: 'FROZEN' },
    { label: 'Archived', value: 'ARCHIVED' },
  ];
  const storeOptions = useMemo(() => {
    const stores = userStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter((store) => store.value === 1);
    if (GaleMarkets?.length) {
      return [
        GaleMarkets[0],
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    } else {
      return [
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
  }, [userStores]);
  const [minEndDate, setMinEndDate] = useState(null);
  const [maxStartDate, setMaxStartDate] = useState(null);

  const handleSelectedFiltersCallback = useCallback(
    (filters) => {
      handleSelectedFilters(filters);
    },
    [handleSelectedFilters]
  );

  useEffect(() => {
    handleSelectedFiltersCallback(selectedFilters);
  }, [handleSelectedFiltersCallback, selectedFilters]);

  const onFilterValueChange = useCallback(
    (name, value) => {
      setSelectedFilters({
        ...selectedFilters,
        [name]: value,
      });
    },
    [setSelectedFilters, selectedFilters]
  );

  const handleSelectBranch = (value) => {
    onFilterValueChange('branch', value);
  };

  const handleSelectStatus = (value) => {
    onFilterValueChange('status', value || '');
  };

  const handleStartDateChange = (value) => {
    const formattedDate = formatDateToMDY(value);
    setMinEndDate(formattedDate);
    onFilterValueChange('startDate', formatDateToMDY(value) || '');
  };

  const handleEndDateChange = (value) => {
    const formattedDate = formatDateToMDY(value);
    setMaxStartDate(formattedDate);
    onFilterValueChange('endDate', formattedDate || '');
  };

  const handlePromotionType = (value) => {
    onFilterValueChange('promotion_type', value);
  };

  const handleResetStartDatePicker = () => {
    setResetStartDate(false);
  };

  const handleResetEndDatePicker = () => {
    setResetEndDate(false);
  };

  const handleResetBranch = () => {
    setResetBranch(false);
  };

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({});
    setResetStartDate(true);
    setResetEndDate(true);
    setResetBranch(true);
    handleClearedFilterState();
    setMinEndDate(null);
    setMaxStartDate(null);
  }, [setSelectedFilters]);

  return (
    <Grid xs={9} item>
      <div className="promotion-overview-filters-container" style={styles.filterContainer}>
        <div style={{ display: 'flex', position: 'relative', top: '7px', right: '7px' }}>
          <SelectDropdown
            label={'Status'}
            options={statusFilters}
            singleSelectDefaultValue={''}
            resetValues={!Object.keys(selectedFilters).length}
            width={'134px'}
            handleFilterChange={handleSelectStatus}
            className="status-promotion-filter"
          />
          {isAdmin ? (
            <SelectDropdown
              label={'Branch'}
              // options={storeOptions}
              // singleSelectDefaultValue={''}
              resetValues={resetBranch}
              isSelectAll={true}
              multiple={true}
              options={storeOptions}
              multiSelectDefaultValues={[]}
              handleFilterChange={handleSelectBranch}
              handleResetValues={handleResetBranch}
              width={'134px'}
              className="branch-promotion-filter"
            />
          ) : null}
        </div>
        <div>
          <DatePickerMui
            label={'Start Date'}
            handleDateChange={handleStartDateChange}
            defaultValue={null}
            resetValue={resetStartDate}
            handleReset={handleResetStartDatePicker}
            maxDate={maxStartDate && new Date(maxStartDate)}
          />
        </div>
        <div>
          <DatePickerMui
            label={'End Date'}
            handleDateChange={handleEndDateChange}
            defaultValue={null}
            resetValue={resetEndDate}
            handleReset={handleResetEndDatePicker}
            minDate={new Date(minEndDate)}
          />
        </div>
        <div style={{ display: 'flex', position: 'relative', top: '7px', right: '7px' }}>
          <SelectDropdown
            label={'Promotion type'}
            options={PROMOTION_TYPES}
            singleSelectDefaultValue={''}
            resetValues={!Object.keys(selectedFilters)?.length}
            width={'98%'}
            handleFilterChange={handlePromotionType}
            className="status-promotion-type-filter"
          />
        </div>
        <ClearFilterButton
          disabled={!Object.keys(selectedFilters).length}
          className="clearButton"
          sx={styles.clearButton}
          onClick={handleClearFilters}
        >
          Clear Filters
        </ClearFilterButton>
      </div>
    </Grid>
  );
};

export default PromotionOverviewFilters;
